/**
 * ============================================================
 * DIBAI 18K — Sección Influencers
 * assets/influencers-dibai.js  v2.0.0
 *
 * - Carrusel drag/swipe/snap con flechas y dots
 * - Scroll continuo tipo marquee (requestAnimationFrame)
 *   sin saltos: duplica el track y hace loop seamless
 * - Lightbox con navegación entre slides
 * - Shopify Theme Editor events
 * ============================================================
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────── */
  const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';
  function getFocusable(el) { return Array.from(el.querySelectorAll(FOCUSABLE)); }

  function trapFocus(e, panel) {
    if (e.key !== 'Tab') return;
    const els = getFocusable(panel).filter(el => !el.closest('[hidden]'));
    if (!els.length) return;
    if (e.shiftKey && document.activeElement === els[0]) {
      e.preventDefault(); els[els.length - 1].focus();
    } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
      e.preventDefault(); els[0].focus();
    }
  }

  function toEmbedUrl(url) {
    if (!url) return null;
    let m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0&playsinline=1`;
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
    m = url.match(/tiktok\.com\/.+\/video\/(\d+)/);
    if (m) return `https://www.tiktok.com/embed/v2/${m[1]}`;
    return null;
  }

  /* ─────────────────────────────────────────
     LIGHTBOX
  ───────────────────────────────────────── */
  class InflLightbox {
    constructor(root, slides) {
      this.root       = root;
      this.slides     = slides;
      this.modal      = root.querySelector('[data-infl-lightbox]');
      this.content    = root.querySelector('[data-infl-lb-content]');
      this.counter    = root.querySelector('[data-infl-lb-counter]');
      this.prevBtn    = root.querySelector('[data-infl-lb-prev]');
      this.nextBtn    = root.querySelector('[data-infl-lb-next]');
      this.current    = 0;
      this._lastFocus = null;
      this._onKey     = this._handleKey.bind(this);
      this.modal?.querySelectorAll('[data-infl-lb-close]').forEach(el => el.addEventListener('click', () => this.close()));
      this.prevBtn?.addEventListener('click', () => this.go(this.current - 1));
      this.nextBtn?.addEventListener('click', () => this.go(this.current + 1));
    }
    open(index) {
      if (!this.modal) return;
      this._lastFocus = document.activeElement;
      this.current = index;
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      this._render();
      document.addEventListener('keydown', this._onKey);
    }
    close() {
      if (!this.modal) return;
      this._stopMedia();
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
      this._lastFocus?.focus();
    }
    go(index) {
      const total = this.slides.length;
      this.current = ((index % total) + total) % total;
      this._stopMedia(); this._render();
    }
    _render() {
      const slide = this.slides[this.current];
      if (!slide || !this.content) return;
      if (this.counter) this.counter.textContent = `${this.current + 1} / ${this.slides.length}`;
      this.content.innerHTML = '';
      if (slide.type === 'video' && slide.video) {
        const embedUrl = toEmbedUrl(slide.video);
        if (embedUrl) {
          const wrap = document.createElement('div'); wrap.className = 'infl-lb-iframe-wrap';
          const iframe = document.createElement('iframe');
          iframe.src = embedUrl; iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true; iframe.title = slide.handle || 'Video';
          wrap.appendChild(iframe); this.content.appendChild(wrap);
        } else if (slide.video.match(/\.(mp4|webm|mov|ogg)(\?.*)?$/i)) {
          const video = document.createElement('video'); video.className = 'infl-lb-video';
          video.controls = true; video.autoplay = true; video.playsinline = true;
          const source = document.createElement('source'); source.src = slide.video;
          video.appendChild(source); this.content.appendChild(video);
        } else {
          const link = document.createElement('a'); link.href = slide.video; link.target = '_blank';
          link.rel = 'noopener noreferrer'; link.textContent = 'Ver video →';
          link.style.cssText = 'color:#fff;font-size:16px;text-decoration:underline;';
          this.content.appendChild(link);
        }
      } else if (slide.image) {
        const img = document.createElement('img'); img.className = 'infl-lb-image';
        img.src = slide.image; img.alt = slide.handle || 'Influencer DIBAI 18K'; img.decoding = 'async';
        this.content.appendChild(img);
      }
      if (slide.handle || slide.caption) {
        const cap = document.createElement('div'); cap.className = 'infl-lb-caption';
        if (slide.handle) {
          const h = document.createElement('span'); h.className = 'infl-lb-caption__handle'; h.textContent = slide.handle; cap.appendChild(h);
          if (slide.verified) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'infl-lb-caption__verified'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16'); svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('fill', 'none');
            svg.innerHTML = '<circle cx="8" cy="8" r="8" fill="#3B82F6"/><path d="M5 8.5l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
            cap.appendChild(svg);
          }
        }
        if (slide.caption) { const p = document.createElement('p'); p.className = 'infl-lb-text'; p.textContent = slide.caption; cap.appendChild(p); }
        this.content.appendChild(cap);
      }
      requestAnimationFrame(() => { getFocusable(this.modal)[0]?.focus(); });
    }
    _stopMedia() {
      this.content?.querySelectorAll('video').forEach(v => { v.pause(); v.src = ''; });
      this.content?.querySelectorAll('iframe').forEach(f => { f.src = ''; });
    }
    _handleKey(e) {
      if (e.key === 'Escape')     { this.close(); return; }
      if (e.key === 'ArrowRight') { this.go(this.current + 1); return; }
      if (e.key === 'ArrowLeft')  { this.go(this.current - 1); return; }
      trapFocus(e, this.modal.querySelector('.infl-lightbox__panel'));
    }
  }

  /* ─────────────────────────────────────────
     MARQUEE — scroll continuo sin saltos
     ─────────────────────────────────────────
     Técnica: el track original se duplica
     (clon) dentro del wrapper. Ambos viajan
     juntos hacia la izquierda. Cuando el
     primero sale completamente de la vista,
     el offset se resetea a 0 de forma
     instantánea → efecto de bucle infinito
     sin ningún corte visible.
  ───────────────────────────────────────── */
  class InflMarquee {
    constructor(root, options = {}) {
      this.root        = root;
      this.speed       = options.speed || 60;       // px por segundo
      this.pauseHover  = options.pauseHover !== false;
      this.carousel    = root.querySelector('[data-infl-carousel]');
      this.track       = root.querySelector('[data-infl-track]');

      if (!this.track || !this.carousel) return;

      this._offset   = 0;
      this._paused   = false;
      this._raf      = null;
      this._lastTime = null;
      this._trackW   = 0;  // ancho de un set de slides (sin duplicado)

      // Ocultar flechas y dots cuando el marquee está activo
      root.querySelectorAll('[data-infl-prev],[data-infl-next],[data-infl-dot]').forEach(el => {
        el.style.display = 'none';
      });

      // El CSS del track original tiene transition; la quitamos para marquee
      this.track.style.transition = 'none';

      // Duplicar el contenido del track para el loop seamless
      this._buildLoop();

      // Medir el ancho de un juego de slides
      this._measure();

      // Eventos de pausa
      if (this.pauseHover) {
        this.carousel.addEventListener('mouseenter', () => { this._paused = true; });
        this.carousel.addEventListener('mouseleave', () => { this._paused = false; });
      }

      // Iniciar el loop
      this._raf = requestAnimationFrame(this._tick.bind(this));

      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._measure(), 150);
      });
    }

    _buildLoop() {
      // Clonar todos los hijos actuales y appendearlos
      const origChildren = Array.from(this.track.children);
      origChildren.forEach(child => {
        const clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('infl-marquee-clone');
        // Quitar listeners de lightbox de los clones (los originales los tienen)
        clone.querySelectorAll('[data-infl-open]').forEach(btn => {
          btn.removeAttribute('data-infl-open');
        });
        this.track.appendChild(clone);
      });
    }

    _measure() {
      // Ancho de los slides originales (primera mitad del track)
      const origSlides = Array.from(this.track.querySelectorAll('.infl-slide:not(.infl-marquee-clone)'));
      if (!origSlides.length) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      let w = 0;
      origSlides.forEach(s => { w += s.offsetWidth + gap; });
      this._trackW = w; // ancho total de un set
    }

    _tick(timestamp) {
      if (this._lastTime === null) this._lastTime = timestamp;
      const delta = (timestamp - this._lastTime) / 1000; // segundos
      this._lastTime = timestamp;

      if (!this._paused && this._trackW > 0) {
        this._offset += this.speed * delta;
        // Cuando el offset supera el ancho del set original, reset instantáneo
        if (this._offset >= this._trackW) {
          this._offset -= this._trackW;
        }
        this.track.style.transform = `translateX(-${this._offset}px)`;
      }

      this._raf = requestAnimationFrame(this._tick.bind(this));
    }

    destroy() {
      if (this._raf) cancelAnimationFrame(this._raf);
    }
  }

  /* ─────────────────────────────────────────
     CARRUSEL (snap / drag / dots) — sin cambios
  ───────────────────────────────────────── */
  class InflCarousel {
    constructor(root, onOpen) {
      this.root      = root;
      this.onOpen    = onOpen;
      this.carousel  = root.querySelector('[data-infl-carousel]');
      this.track     = root.querySelector('[data-infl-track]');
      this.slides    = Array.from(root.querySelectorAll('.infl-slide'));
      this.dots      = Array.from(root.querySelectorAll('[data-infl-dot]'));
      this.prevBtn   = root.querySelector('[data-infl-prev]');
      this.nextBtn   = root.querySelector('[data-infl-next]');
      this.current   = 0;
      this._offset   = 0;
      this._startX   = 0;
      this._startOff = 0;
      this._dragging = false;
      this._touchSX  = 0;
      this._touchSY  = 0;

      if (!this.track || !this.slides.length) return;
      this._measure();
      this._bindArrows();
      this._bindDots();
      this._bindDrag();
      this._bindTouch();
      this._bindOpenButtons();
      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._measure(), 200);
      });
    }
    _measure() {
      const style = getComputedStyle(this.root);
      const w = this.carousel.clientWidth;
      const gap = parseFloat(style.getPropertyValue('--infl-gap')) || 16;
      const vw = window.innerWidth;
      const cols = vw < 750
        ? parseFloat(style.getPropertyValue('--infl-cols-mobile')) || 2
        : vw < 990
          ? parseFloat(style.getPropertyValue('--infl-cols-tablet')) || 3
          : parseFloat(style.getPropertyValue('--infl-cols-desktop')) || 5;
      this._cols   = Math.round(cols);
      this._gap    = gap;
      this._stepW  = (w - gap * (this._cols - 1)) / this._cols + gap;
      this._maxOff = Math.max(0, (this.slides.length - this._cols) * this._stepW);
      this._offset = Math.min(this._offset, this._maxOff);
      this._apply(false);
    }
    _apply(animate = true) {
      if (!animate) this.track.classList.add('no-transition');
      this.track.style.transform = `translateX(-${this._offset}px)`;
      if (!animate) requestAnimationFrame(() => requestAnimationFrame(() => this.track.classList.remove('no-transition')));
    }
    goTo(index) {
      const total = this.slides.length - this._cols;
      if (total <= 0) return;
      index = ((index % (total + 1)) + (total + 1)) % (total + 1);
      this.current = index;
      this._offset = Math.min(index * this._stepW, this._maxOff);
      this._apply(true);
      this._updateDots(index);
    }
    _updateDots(idx) {
      this.dots.forEach((d, i) => {
        const active = i === idx;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', String(active));
        d.setAttribute('tabindex', active ? '0' : '-1');
      });
    }
    _bindArrows() {
      this.prevBtn?.addEventListener('click', () => this.goTo(this.current - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.current + 1));
    }
    _bindDots() {
      this.dots.forEach(d => d.addEventListener('click', () => this.goTo(parseInt(d.dataset.inflDot, 10))));
    }
    _bindOpenButtons() {
      this.root.querySelectorAll('[data-infl-open]').forEach(btn => {
        btn.addEventListener('click', () => this.onOpen(parseInt(btn.dataset.inflOpen, 10)));
      });
    }
    _bindDrag() {
      if (!this.carousel) return;
      this.carousel.addEventListener('mousedown', e => {
        this._dragging = true; this._startX = e.clientX; this._startOff = this._offset;
        this.carousel.classList.add('is-dragging'); this.track.classList.add('no-transition');
      });
      window.addEventListener('mousemove', e => {
        if (!this._dragging) return;
        const dx = this._startX - e.clientX;
        this._offset = Math.max(0, Math.min(this._maxOff, this._startOff + dx));
        this.track.style.transform = `translateX(-${this._offset}px)`;
      });
      window.addEventListener('mouseup', () => {
        if (!this._dragging) return;
        this._dragging = false;
        this.carousel.classList.remove('is-dragging'); this.track.classList.remove('no-transition');
        this.goTo(Math.round(this._offset / this._stepW));
      });
    }
    _bindTouch() {
      if (!this.carousel) return;
      this.carousel.addEventListener('touchstart', e => {
        this._touchSX = e.touches[0].clientX; this._touchSY = e.touches[0].clientY;
        this._startOff = this._offset; this.track.classList.add('no-transition');
      }, { passive: true });
      this.carousel.addEventListener('touchmove', e => {
        const dx = this._touchSX - e.touches[0].clientX;
        const dy = this._touchSY - e.touches[0].clientY;
        if (Math.abs(dy) > Math.abs(dx)) return;
        this._offset = Math.max(0, Math.min(this._maxOff, this._startOff + dx));
        this.track.style.transform = `translateX(-${this._offset}px)`;
      }, { passive: true });
      this.carousel.addEventListener('touchend', e => {
        this.track.classList.remove('no-transition');
        const dx = this._touchSX - e.changedTouches[0].clientX;
        if (Math.abs(dx) > 40) { dx > 0 ? this.goTo(this.current + 1) : this.goTo(this.current - 1); }
        else { this.goTo(Math.round(this._offset / this._stepW)); }
      }, { passive: true });
    }
  }

  /* ─────────────────────────────────────────
     INICIALIZACIÓN
  ───────────────────────────────────────── */
  function initSection(root) {
    if (!root) return;

    const sectionEl = root.querySelector('.infl-section');
    const dataEl    = root.querySelector('[id^="infl-data-"]');
    let slides = [];
    try { slides = JSON.parse(dataEl?.textContent || '[]'); }
    catch (e) { console.warn('[InflDibai] JSON inválido', e); }

    const lb = new InflLightbox(root, slides);

    // Leer opciones de marquee del data-attribute del section
    const marqueeEnabled = sectionEl?.dataset.marquee === 'true';
    const marqueeSpeed   = parseFloat(sectionEl?.dataset.marqueeSpeed || '60');
    const marqueePause   = sectionEl?.dataset.marqueePause !== 'false';

    if (marqueeEnabled) {
      root._instance = new InflMarquee(root, {
        speed:      marqueeSpeed,
        pauseHover: marqueePause
      });
      // En modo marquee, vincular lightbox solo a slides originales (no clones)
      root.querySelectorAll('[data-infl-open]').forEach(btn => {
        btn.addEventListener('click', () => lb.open(parseInt(btn.dataset.inflOpen, 10)));
      });
    } else {
      root._instance = new InflCarousel(root, idx => lb.open(idx));
    }
  }

  function initAll() {
    document.querySelectorAll('.influencers-dibai-wrapper').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => {
    const root = e.target.closest('.influencers-dibai-wrapper') || e.target.querySelector('.influencers-dibai-wrapper');
    if (!root) return;
    root._instance?.destroy?.();
    delete root._instance;
    initSection(root);
  });

  document.addEventListener('shopify:section:unload', e => {
    const root = e.target.querySelector?.('.influencers-dibai-wrapper');
    root?._instance?.destroy?.();
    e.target.querySelector?.('[data-infl-lightbox]:not([hidden])')?.setAttribute('hidden', '');
    document.body.style.overflow = '';
  });

})();
